import { CollectibleEffect, type CollectibleDefinition } from './CollectibleDefinition';
import { CollectibleType } from './CollectibleType';

const PICKUP_VOLUME = { center: { x: 0, y: 0.35, z: 0 }, size: { x: 1, y: 1.1, z: 1 } } as const;

export const DEFAULT_COLLECTIBLES: readonly CollectibleDefinition[] = [
  {
    id: 'COL_BEER_BOTTLE_A',
    type: CollectibleType.BeerBottle,
    displayName: 'Bierflasche',
    visualAssetId: 'COL_BEER_BOTTLE_A',
    effect: CollectibleEffect.AddBottles,
    collider: PICKUP_VOLUME,
    spins: true,
  },
  {
    id: 'COL_BEER_CRATE_A',
    type: CollectibleType.BeerCrate,
    displayName: 'Bierkiste (Turbo)',
    visualAssetId: 'COL_BEER_CRATE_A',
    effect: CollectibleEffect.Turbo,
    collider: PICKUP_VOLUME,
    spins: true,
    // hangs where it is hard to reach (roofs, high lines) – a magnet must not bring it down
    magnetic: false,
  },
  {
    id: 'COL_GOLDEN_BOTTLE_A',
    type: CollectibleType.GoldenBottle,
    displayName: 'Goldene Flasche',
    visualAssetId: 'COL_GOLDEN_BOTTLE_A',
    effect: CollectibleEffect.AddBottles,
    collider: PICKUP_VOLUME,
    spins: true,
    // has to be reached – a double jump from the ramp (SEG_GOLDEN_RAMP_60M), never pulled in
    magnetic: false,
  },
  {
    id: 'COL_TOOLBOX_A',
    type: CollectibleType.Toolbox,
    displayName: 'Werkzeugkiste',
    visualAssetId: 'COL_TOOLBOX_A',
    effect: CollectibleEffect.AddBottles,
    collider: PICKUP_VOLUME,
    spins: true,
  },
  {
    id: 'COL_EXTRA_LIFE_A',
    type: CollectibleType.ExtraLife,
    displayName: 'Bonus-Helm (Extra-Leben)',
    visualAssetId: 'COL_EXTRA_LIFE_A',
    effect: CollectibleEffect.AddExtraLife,
    collider: PICKUP_VOLUME,
    spins: true,
  },
  {
    id: 'COL_MAGNET_A',
    type: CollectibleType.Magnet,
    displayName: 'Magnet',
    visualAssetId: 'COL_MAGNET_A',
    effect: CollectibleEffect.Magnet,
    collider: PICKUP_VOLUME,
    spins: true,
  },
  {
    id: 'COL_TIME_BONUS_A',
    type: CollectibleType.TimeBonus,
    displayName: 'Zeitbonus',
    visualAssetId: 'COL_TIME_BONUS_A',
    effect: CollectibleEffect.TimeBonus,
    collider: PICKUP_VOLUME,
    spins: true,
  },
];

export class CollectibleCatalog {
  private readonly byId = new Map<string, CollectibleDefinition>();

  constructor(definitions: readonly CollectibleDefinition[]) {
    for (const definition of definitions) {
      if (this.byId.has(definition.id)) throw new Error(`Duplicate collectible id ${definition.id}`);
      this.byId.set(definition.id, definition);
    }
  }

  get(id: string): CollectibleDefinition {
    const definition = this.byId.get(id);
    if (!definition) throw new Error(`Unknown collectible id ${id}`);
    return definition;
  }

  /** First definition of a type – the default visual variant. */
  byType(type: CollectibleType): CollectibleDefinition {
    for (const definition of this.byId.values()) if (definition.type === type) return definition;
    throw new Error(`No collectible of type ${type}`);
  }

  all(): readonly CollectibleDefinition[] {
    return [...this.byId.values()];
  }
}
